import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Loader, Text } from '../shared-components';
import { colors } from '../shared-components/styles';
import { useAssistantChat } from './context/AssistantChatProvider';

export const AssistantChatThread = () => {
  // Importing the context to access the chat thread and loading state.
  const { thread, isLoading } = useAssistantChat();

  // Ref to scroll to the end of the chat history.
  const chatEndRef = useRef(null);

  // Effect to scroll to the end of the chat history whenever it changes.
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread]);

  if (isLoading) {
    return (
      <Box
        css={`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        `}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        gap: 16px;
      `}
    >
      {thread.map((message, index) => (
        <Box
          key={index}
          css={`
            display: flex;
            justify-content: ${message.sender === 'user' ? 'flex-end' : 'flex-start'};
          `}
        >
          {message.sender === 'user' ? (
            <AssistantChatThread.UserMessage message={message} />
          ) : (
            <AssistantChatThread.AssistantMessage message={message} />
          )}
        </Box>
      ))}
      <Box
        ref={chatEndRef}
        css={`
          padding-bottom: 12px;
        `}
      />
    </Box>
  );
};

AssistantChatThread.UserMessage = ({ message }) => {
  return (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
        width: 75%;
      `}
    >
      <Box
        css={`
          padding: 12px 16px;
          background: white;
          border: 1px solid ${colors.gray[300]};
          border-radius: 8px;
        `}
      >
        <Text label>{message.text}</Text>
      </Box>
      <Text helper color={colors.gray[400]}>
        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Text>
    </Box>
  );
};

AssistantChatThread.AssistantMessage = ({ message }) => {
  return (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0px 4px;
        gap: 8px;
      `}
    >
      <Text helper color={colors.gray[400]}>
        Answer
      </Text>
      <Box>
        <ReactMarkdown
          components={{
            p: ({ node, ...props }) => <Text {...props} label />,
          }}
          children={message.text}
        />
      </Box>
    </Box>
  );
};
